import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { AlarmClock, AlertTriangle, Anchor, Bell, Cake, CalendarDays, Car, ClipboardList, CloudSun, GraduationCap, ListChecks, StickyNote, Wrench, X } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { isAdmin } from "../lib/roles";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

let ctx = null;
const tone = (freq, start, dur, type = "sine", vol = 0.25) => {
  const o = ctx.createOscillator(); const g = ctx.createGain();
  o.type = type; o.frequency.value = freq;
  g.gain.setValueAtTime(0.0001, ctx.currentTime + start);
  g.gain.exponentialRampToValueAtTime(vol, ctx.currentTime + start + 0.02);
  g.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + start + dur);
  o.connect(g).connect(ctx.destination); o.start(ctx.currentTime + start); o.stop(ctx.currentTime + start + dur + 0.05);
};
export function playSound(kind) {
  try {
    ctx = ctx || new (window.AudioContext || window.webkitAudioContext)();
    if (ctx.state === "suspended") ctx.resume();
    if (kind === "alert") { [0, 0.3, 0.6, 0.9].forEach((t, i) => tone(i % 2 ? 660 : 880, t, 0.28, "square", 0.15)); }
    else if (kind === "barrier") { [0, 0.7].forEach((t) => { tone(880, t, 0.25, "square", 0.18); tone(1175, t + 0.25, 0.3, "square", 0.18); }); }
    else { tone(988, 0, 0.35); tone(1319, 0.18, 0.5); }
  } catch { /* audio not available */ }
}

const ICONS = { alert: AlertTriangle, report: ClipboardList, compito: ListChecks, meteo: CloudSun, manutenzione: Wrench, scadenza_personale: GraduationCap, compleanno: Cake, turni: CalendarDays, parcheggio: Car, ormeggio: Anchor, postit: StickyNote, sveglia: AlarmClock };
const target = (n, user) => {
  if (n.type === "alert") return n.ref_id ? `/alert/${n.ref_id}` : "/avvisi";
  if (n.type === "report") return n.source === "web" || isAdmin(user) ? "/report/turni" : "/report";
  if (n.type === "compito") return isAdmin(user) ? "/admin/tasks" : "/nostromo/tasks";
  if (n.type?.startsWith("ferie")) return isAdmin(user) ? "/admin/leaves" : "/turni";
  if (n.type === "meteo") return "/meteo";
  if (n.type === "ormeggio") return isAdmin(user) ? "/admin/sito?tab=prenotazioni" : "/richieste-ormeggio";
  if (n.type === "manutenzione") return "/manutenzioni";
  if (n.type === "scadenza_personale") return isAdmin(user) ? "/admin/certifications" : "/corsi";
  if (n.type === "compleanno") return isAdmin(user) ? "/admin/birthdays" : null;
  if (n.type === "turni") return "/turni";
  if (n.type === "parcheggio") return "/parcheggi";
  if (n.type === "postit") return "/postit";
  if (n.type === "sveglia") return "/turni";
  return null;
};
function setBadge(count) {
  try {
    if (!("setAppBadge" in navigator)) return;
    if (count > 0) navigator.setAppBadge(count).catch(() => {}); else navigator.clearAppBadge().catch(() => {});
  } catch { /* badge not supported */ }
}

async function systemNotify(fresh, user) {
  if (!("Notification" in window) || Notification.permission !== "granted" || !("serviceWorker" in navigator)) return;
  const reg = await navigator.serviceWorker.ready.catch(() => null);
  if (!reg) return;
  fresh.slice(0, 3).forEach((n) => reg.showNotification(n.title || "Porto di Roma Crew", {
    body: n.body || "", icon: "/icon-192.png", badge: "/favicon.png", tag: n.id, renotify: true,
    requireInteraction: n.type === "alert", vibrate: n.type === "alert" ? [300, 150, 300, 150, 300] : [200, 100, 200],
    data: { url: target(n, user) || "/" },
  }).catch(() => {}));
}

export function NotifyPermission() {
  const supported = "Notification" in window && "serviceWorker" in navigator;
  const [perm, setPerm] = useState(supported ? Notification.permission : "unsupported");
  const ask = async () => { const p = await Notification.requestPermission(); setPerm(p); if (p === "granted") playSound("notifica"); };
  return (
    <div className="mt-4 pt-4 border-t" data-testid="notify-permission">
      <p className="font-semibold text-[var(--navy)] text-sm">Avvisi sul dispositivo</p>
      {perm === "granted" ? <p className="text-sm text-emerald-700 mt-1" data-testid="notify-permission-on">Attivi: con l'app aperta o in secondo piano ricevi avviso, suono e numero sull'icona.</p>
        : perm === "denied" ? <p className="text-sm text-red-600 mt-1" data-testid="notify-permission-denied">Bloccati: riattivali dalle impostazioni del browser o del telefono per questa app.</p>
        : perm === "unsupported" ? <p className="text-sm text-slate-600 mt-1" data-testid="notify-permission-unsupported">Questo browser non supporta gli avvisi. Su iPhone aggiungi prima l'app alla schermata Home.</p>
        : (<><p className="text-sm text-slate-600 mt-1">Ricevi avviso, suono e numero sull'icona quando arrivano Alert e notifiche.</p>
          <button onClick={ask} className="mt-3 inline-flex items-center gap-2 rounded-full bg-[var(--teal)] text-white text-sm font-medium px-4 py-2 hover:opacity-90 transition-opacity" data-testid="notify-permission-button"><Bell className="w-4 h-4" />Attiva avvisi</button></>)}
    </div>
  );
}

const ago = (s) => new Date(s).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });

function useNotifications(user) {
  const [list, setList] = useState([]);
  const [unread, setUnread] = useState(0);
  const seen = useRef(null);
  const load = useCallback(() => Promise.all([api("/notifications"), api("/web/notifications")].map((p) => p.then((d) => d.notifications || []).catch(() => []))).then((parts) => {
    const items = parts.flat().sort((a, b) => (b.created_at || "").localeCompare(a.created_at || ""));
    const fresh = items.filter((n) => !n.read && seen.current && !seen.current.has(n.id));
    if (fresh.length) playSound(fresh.some((n) => n.type === "alert") ? "alert" : "notifica");
    if (fresh.length) systemNotify(fresh, user);
    seen.current = new Set(items.map((n) => n.id));
    const count = items.filter((n) => !n.read).length;
    setBadge(count);
    setList(items); setUnread(count);
  }).catch(() => {}), [user]);
  useEffect(() => {
    seen.current = null; load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load, user?.id]);
  const markRead = async (ids) => {
    if (!ids.length) return;
    const web = list.filter((n) => n.source === "web" && ids.includes(n.id)).map((n) => n.id);
    const mobile = ids.filter((i) => !web.includes(i));
    setList((l) => l.map((n) => (ids.includes(n.id) ? { ...n, read: true } : n))); setUnread((u) => { const c = Math.max(0, u - ids.length); setBadge(c); return c; });
    if (mobile.length) await api("/notifications/mark-read", { method: "POST", body: { ids: mobile } }).catch(() => {});
    if (web.length) await api("/web/notifications/mark-read", { method: "POST", body: { ids: web } }).catch(() => {});
  };
  const dismiss = async (n) => {
    setList((l) => l.filter((x) => x.id !== n.id));
    if (!n.read) setUnread((u) => { const c = Math.max(0, u - 1); setBadge(c); return c; });
    await api(n.source === "web" ? "/web/notifications/mark-read" : "/notifications/dismiss", { method: "POST", body: { ids: [n.id] } }).catch(() => {});
  };
  return { list, unread, markRead, dismiss };
}

export function NotificationBell({ dark }) {
  const desktop = useDesktop();
  if (desktop === !!dark) return null;
  return <Bell_ dark={dark} />;
}

function useDesktop() {
  const q = "(min-width: 1024px)";
  const [m, setM] = useState(() => window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q); const on = () => setM(mq.matches);
    mq.addEventListener("change", on);
    return () => mq.removeEventListener("change", on);
  }, []);
  return m;
}

function Bell_({ dark }) {
  const { user } = useAuth();
  const nav = useNavigate();
  const [open, setOpen] = useState(false);
  const { list, unread, markRead, dismiss } = useNotifications(user);
  const ring = list.find((n) => (n.type === "postit" || n.type === "sveglia") && !n.read);
  const pick = (n) => { if (!n.read) markRead([n.id]); const to = target(n, user); setOpen(false); if (to) nav(to); };
  return (
    <>
    {ring && <PostitAlarm n={ring} onClose={() => markRead([ring.id])} />}
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button className={`relative p-2 rounded-full transition-colors ${dark ? "text-white hover:bg-white/10" : "text-[var(--navy)] bg-white shadow-sm border hover:bg-slate-50"}`} aria-label="Notifiche" data-testid={dark ? "mobile-notifications-bell" : "notifications-bell"}>
          <Bell className="w-5 h-5" strokeWidth={1.8} />
          {unread > 0 && <span className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-600 text-white text-[10px] font-bold flex items-center justify-center" data-testid="notifications-count">{unread > 99 ? "99+" : unread}</span>}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-[min(92vw,380px)] p-0 rounded-2xl overflow-hidden" data-testid="notifications-panel">
        <div className="flex items-center justify-between px-4 py-3 border-b bg-[var(--navy)] text-white">
          <p className="font-semibold">Notifiche {unread > 0 && <span className="text-white/60 font-normal">· {unread} da leggere</span>}</p>
          {unread > 0 && <button onClick={() => markRead(list.filter((n) => !n.read).map((n) => n.id))} className="text-xs underline text-white/80 hover:text-white" data-testid="notifications-read-all">Segna tutte come lette</button>}
        </div>
        <div className="max-h-[60vh] overflow-y-auto">
          {list.length === 0 ? <p className="p-6 text-center text-sm text-slate-500">Nessuna notifica</p> : list.map((n) => {
            const Icon = ICONS[n.type] || (n.type?.startsWith("ferie") ? CalendarDays : Bell);
            return (
              <div key={n.id} role="button" tabIndex={0} onClick={() => pick(n)} onKeyDown={(e) => { if (e.key === "Enter") pick(n); }} className={`group w-full text-left flex gap-3 px-4 py-3 border-b last:border-0 hover:bg-slate-50 transition-colors cursor-pointer ${n.read ? "" : "bg-[var(--teal-soft)]/50"}`} data-testid={`notification-${n.id}`}>
                <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${n.type === "alert" ? "text-red-600" : "text-[var(--teal)]"}`} />
                <div className="min-w-0 flex-1">
                  <p className={`text-sm text-[var(--navy)] ${n.read ? "" : "font-semibold"}`}>{n.title}</p>
                  {n.body && <p className="text-xs text-slate-600 line-clamp-2">{n.body}</p>}
                  <p className="text-[11px] text-slate-400 mt-0.5">{ago(n.created_at)}</p>
                </div>
                {!n.read && <span className="w-2 h-2 rounded-full bg-red-600 mt-1.5 shrink-0" />}
                <button type="button" onClick={(e) => { e.stopPropagation(); dismiss(n); }} className="p-1 -mr-1 -mt-0.5 h-fit rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 shrink-0" aria-label="Elimina notifica" title="Elimina" data-testid={`notification-dismiss-${n.id}`}><X className="w-4 h-4" /></button>
              </div>);
          })}
        </div>
      </PopoverContent>
    </Popover>
    </>
  );
}

function PostitAlarm({ n, onClose }) {
  const [testo, setTesto] = useState(n.body);
  useEffect(() => {
    if (n.type !== "postit") { return; }
    api("/postits").then((d) => { const p = (d.postits || []).find((x) => x.id === n.ref_id); if (p) setTesto(p.testo); }).catch(() => {});
  }, [n.ref_id, n.type]);
  useEffect(() => {
    playSound("alert");
    const t = setInterval(() => playSound("alert"), 1500);
    return () => clearInterval(t);
  }, [n.id]);
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 p-4" role="alertdialog" data-testid="postit-alarm-dialog">
      <div className="w-full max-w-md rounded-2xl bg-[#FEF3A8] p-6 shadow-2xl animate-in zoom-in-95">
        <p className="flex items-center gap-2 font-display font-bold text-[#3b3415] text-lg"><AlarmClock className="w-6 h-6 animate-pulse text-red-600" />{n.type === "sveglia" ? "Sveglia turno" : "Promemoria post-it"}</p>
        <p className="mt-4 whitespace-pre-wrap text-[#3b3415] max-h-[50vh] overflow-y-auto" data-testid="postit-alarm-text">{testo}</p>
        <button onClick={onClose} className="mt-6 w-full rounded-full bg-[var(--navy)] text-white font-semibold py-3 hover:opacity-90 transition-opacity" data-testid="postit-alarm-close">Chiudi</button>
      </div>
    </div>
  );
}
